import React, { useState, useEffect } from 'react';
import { UserX, UserCheck, Search, Shield, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlockUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [actionModal, setActionModal] = useState(null);
  const [reason, setReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [userTypeFilter, statusFilter]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/user-management/users?`;
      if (userTypeFilter) url += `user_type=${userTypeFilter}&`;
      if (statusFilter) url += `status=${statusFilter}&`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUnblock = async () => {
    if (!actionModal) return;

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/user-management/block-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: actionModal.user.id,
          user_type: actionModal.user.user_type,
          action: actionModal.action,
          reason: reason
        })
      });

      if (response.ok) {
        toast.success(`✅ User ${actionModal.action === 'block' ? 'blocked' : 'unblocked'} successfully!`);
        setActionModal(null);
        setReason('');
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Server error');
    } finally {
      setProcessing(false);
    }
  };

  const filteredUsers = users.filter(user => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      (user.full_name || '').toLowerCase().includes(searchLower) ||
      (user.username || '').toLowerCase().includes(searchLower)
    );
  });

  const activeCount = users.filter(u => u.is_active).length;
  const blockedCount = users.filter(u => !u.is_active).length;

  return (
    <div className="p-6 space-y-6" data-testid="block-users-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Block/Unblock Users</h1>
        <p className="text-gray-500">Manage user access to the system</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 flex items-center gap-4">
            <UserCheck className="h-10 w-10 text-green-600" />
            <div>
              <p className="text-2xl font-bold text-green-700">{activeCount}</p>
              <p className="text-sm text-green-600">Active Users</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4 flex items-center gap-4">
            <UserX className="h-10 w-10 text-red-600" />
            <div>
              <p className="text-2xl font-bold text-red-700">{blockedCount}</p>
              <p className="text-sm text-red-600">Blocked Users</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search user..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg"
                />
              </div>
            </div>
            <select
              value={userTypeFilter}
              onChange={(e) => setUserTypeFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="">All Types</option>
              <option value="staff">Staff</option>
              <option value="student">Student</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="">All Status</option>
              <option value="active">Active Only</option>
              <option value="blocked">Blocked Only</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle>Users ({filteredUsers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No users found</div>
          ) : (
            <div className="space-y-2">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className={`p-4 rounded-lg border flex items-center justify-between ${
                    user.is_active ? 'bg-white' : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      user.is_active ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {user.is_active ? (
                        <UserCheck className="h-5 w-5 text-green-600" />
                      ) : (
                        <UserX className="h-5 w-5 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{user.full_name || 'N/A'}</p>
                      <p className="text-sm text-gray-500">
                        {user.username} • {user.user_type === 'staff' ? 'Staff' : 'Student'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      user.is_active 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {user.is_active ? 'Active' : 'Blocked'}
                    </span>
                    
                    {user.is_active ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => setActionModal({ user, action: 'block' })}
                      >
                        <UserX className="h-4 w-4 mr-1" />
                        Block
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 border-green-200 hover:bg-green-50"
                        onClick={() => setActionModal({ user, action: 'unblock' })}
                      >
                        <UserCheck className="h-4 w-4 mr-1" />
                        Unblock
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Modal */}
      {actionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 m-4">
            <div className="flex items-center gap-3 mb-4">
              {actionModal.action === 'block' ? (
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
              ) : (
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Shield className="h-6 w-6 text-green-600" />
                </div>
              )}
              <div>
                <h3 className="text-lg font-bold">
                  {actionModal.action === 'block' ? 'Block User' : 'Unblock User'}
                </h3>
                <p className="text-sm text-gray-500">{actionModal.user.full_name}</p>
              </div>
            </div>

            <p className="text-gray-600 mb-4">
              {actionModal.action === 'block' 
                ? 'This user will not be able to login until unblocked.'
                : 'This user will be able to login again.'
              }
            </p>

            {actionModal.action === 'block' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason (optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter reason for blocking..."
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                />
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setActionModal(null); setReason(''); }}
              >
                Cancel
              </Button>
              <Button
                className={`flex-1 ${
                  actionModal.action === 'block' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
                onClick={handleBlockUnblock}
                disabled={processing}
              >
                {processing ? 'Processing...' : actionModal.action === 'block' ? 'Block User' : 'Unblock User'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
